/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { metaMcpUpdate } from "../funcs/metaMcpUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { MetaMcpServer } from "../models/components/metamcpserver.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateMetaMcpServerRequest,
  UpdateMetaMcpServerSecurity,
} from "../models/operations/updatemetamcpserver.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateMetaMcpServerMutationVariables = {
  request: UpdateMetaMcpServerRequest;
  security?: UpdateMetaMcpServerSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateMetaMcpServerMutationData = MetaMcpServer;

export type UpdateMetaMcpServerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateMetaMcpServer metaMcp
 *
 * @remarks
 * Update a meta MCP server. Omitting user_session_issuer_id preserves the stored issuer, and a gateway that would end up without one gets a dedicated issuer minted — an update can never leave a gateway serving anonymously. Omitting visibility or network_access_mode preserves the respective stored value, so a caller that does not manage those policies cannot change them by saving an unrelated field.
 */
export function useUpdateMetaMcpServerMutation(
  options?: MutationHookOptions<
    UpdateMetaMcpServerMutationData,
    UpdateMetaMcpServerMutationError,
    UpdateMetaMcpServerMutationVariables
  >,
): UseMutationResult<
  UpdateMetaMcpServerMutationData,
  UpdateMetaMcpServerMutationError,
  UpdateMetaMcpServerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateMetaMcpServerMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateMetaMcpServer(): MutationKey {
  return ["@gram/client", "metaMcp", "update"];
}

export function buildUpdateMetaMcpServerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateMetaMcpServerMutationVariables,
  ) => Promise<UpdateMetaMcpServerMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateMetaMcpServer(),
    mutationFn: function updateMetaMcpServerMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateMetaMcpServerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(metaMcpUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
